import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';

const USER_MANAGEMENT_API = '/umbraco/api/UserManagment';

type NestedApiResult = {
  Code?: number;
  data?: {
    Code?: number;
    Data?: string;
    Error?: string;
  };
};

/** Normalized shape used by the UI: a single message + success flag. */
export type UserManagementResult = {
  success: boolean;
  message: string;
};

function normalizeResult(response: NestedApiResult): UserManagementResult {
  const inner = response.data;
  const code = inner?.Code ?? response.Code ?? 0;

  if (code < 1) {
    return {
      success: false,
      message: inner?.Error || 'Operation failed',
    };
  }

  return {
    success: true,
    message: inner?.Data || 'Operation completed successfully',
  };
}

export const userManagementApi = createApi({
  reducerPath: 'userManagementApi',
  baseQuery,
  tagTypes: ['UserGroups'],
  endpoints: (builder) => ({
    getUmbracoUserGroups: builder.query<string[], { relationKeys: string[] }>({
      query: ({ relationKeys }) => {
        const params = new URLSearchParams();
        relationKeys.forEach((key) => params.append('relationKeys', key));
        return {
          url: `${USER_MANAGEMENT_API}/GetUmbracoUserGroups?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: unknown) => {
        if (Array.isArray(response)) return response as string[];
        const wrapped = response as { data?: string[] };
        return wrapped?.data ?? [];
      },
      providesTags: (_result, _error, { relationKeys }) => [
        { type: 'UserGroups', id: relationKeys.join(',') },
      ],
    }),

    editRoles: builder.mutation<
      UserManagementResult,
      { nodeKey: string; relationKeys: string[]; roles: string[] }
    >({
      query: (body) => ({
        url: `${USER_MANAGEMENT_API}/EditRoles`,
        method: 'POST',
        body,
      }),
      transformResponse: normalizeResult,
    }),

    setupLogin: builder.mutation<
      UserManagementResult,
      {
        nodeKey: string;
        keys: string[];
        roles: string;
        enable2Fa: boolean;
        useSpaActivation: boolean;
      }
    >({
      query: (body) => ({
        url: `${USER_MANAGEMENT_API}/SetupLogin`,
        method: 'POST',
        body,
      }),
      transformResponse: normalizeResult,
    }),
  }),
});

export const { useGetUmbracoUserGroupsQuery, useEditRolesMutation, useSetupLoginMutation } =
  userManagementApi;
